<!--
  Sin `dismissableMask`: hay trabajo sin guardar (arrendamientos elegidos, acta subida) y un clic
  fuera no debe tirarlo. X y Escape cierran. `appendTo="body"`: la tarjeta del cascarón tiene
  `overflow: hidden` y atraparía el modal (ui-styleguide §3.12).
-->
<p-dialog
  [visible]="visible"
  (visibleChange)="onVisibleChange($event)"
  header="Nueva sesión del comité"
  [modal]="true"
  [dismissableMask]="false"
  [draggable]="false"
  [resizable]="false"
  [closable]="!submitting"
  [closeOnEscape]="false"
  appendTo="body"
  styleClass="ns-dialog"
  [style]="{ width: '560px' }"
>
  <!-- Escape lo decide el componente (QA-11): con un desplegable abierto solo se cierra el desplegable. -->
  <div class="ns-form" (keydown.escape)="onEscape($event)">
    <!-- Tipo: votación o registro de acta. En Cerrados solo hay registro. -->
    <div class="field field-flex">
      <label id="nsKindLabel">*Tipo de sesión:</label>
      <div class="ns-kind" role="radiogroup" aria-labelledby="nsKindLabel">
        <button
          *ngFor="let k of kindOptions"
          type="button"
          role="radio"
          class="ns-kind-btn"
          [class.is-on]="kind === k.value"
          [attr.aria-checked]="kind === k.value"
          [disabled]="submitting"
          (click)="kind = k.value; onKindChange()"
        >
          <i [class]="k.value === 'voting' ? 'fa-solid fa-check-to-slot' : 'fa-regular fa-file-lines'" aria-hidden="true"></i>
          {{ k.label }}
        </button>
      </div>
      <small class="ns-hint" *ngIf="isVoting"
        >Abre una reunión por arrendamiento, avisa por correo a los votantes y Legal la cierra cuando hay votos.</small
      >
      <small class="ns-hint" *ngIf="!isVoting"
        >Registra un acta histórica: los comités quedan <b>Aprobados</b> y el estado del arrendamiento no cambia.</small
      >
    </div>

    <div class="field field-flex">
      <label for="nsDate">*Fecha y hora de la sesión:</label>
      <p-calendar
        inputId="nsDate"
        [(ngModel)]="sessionDate"
        [showTime]="true"
        dateFormat="dd/mm/yy"
        [showIcon]="true"
        [maxDate]="today"
        [disabled]="submitting"
        appendTo="body"
        styleClass="w-full"
      ></p-calendar>
      <small class="ns-hint">{{
        isVoting ? 'Cuándo sesiona el comité. No se agenda a futuro.' : 'La fecha del acta que se registra.'
      }}</small>
    </div>

    <!-- Votantes: solo en votación. Todos preseleccionados; Legal quita, no agrega uno por uno. -->
    <div class="field field-flex" *ngIf="isVoting">
      <label for="nsVoters">*Miembros votantes:</label>
      <p-multiSelect
        inputId="nsVoters"
        [(ngModel)]="selectedVoters"
        [options]="memberOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Selecciona los miembros del comité"
        selectedItemsLabel="{0} votantes"
        [maxSelectedLabels]="2"
        [filter]="true"
        [disabled]="submitting"
        appendTo="body"
        styleClass="w-full"
        emptyMessage="No hay miembros del comité elegibles"
        emptyFilterMessage="Ningún miembro coincide"
      ></p-multiSelect>
      <small class="ns-hint" *ngIf="membersLoaded && !memberOptions.length"
        >No hay miembros con «isCommitteeMember». Márcalos en Administradores.</small
      >
    </div>

    <!-- Arrendamientos: los que llegaron por la dirección, más los que se agreguen por folio. -->
    <div class="field field-flex">
      <label for="nsLeases">*Arrendamientos ({{ leases.length }}):</label>
      <div class="ns-chips" *ngIf="leases.length">
        <span class="ns-chip" *ngFor="let l of leases">
          <b>{{ l.folio }}</b>
          <span class="ns-chip-client" *ngIf="l.clientName">· {{ l.clientName }}</span>
          <button
            type="button"
            class="ns-chip-x"
            [attr.aria-label]="'Quitar ' + l.folio"
            [disabled]="submitting"
            (click)="removeLease(l._id)"
          >
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </span>
      </div>
      <p class="ns-hint" *ngIf="resolving"><i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Cargando los arrendamientos…</p>
      <p-autoComplete
        inputId="nsLeases"
        [(ngModel)]="leaseQuery"
        [suggestions]="suggestions"
        (completeMethod)="search($event)"
        (onSelect)="addLease($event.value)"
        field="folio"
        [minLength]="2"
        [delay]="300"
        placeholder="Agregar por folio o cliente…"
        [disabled]="submitting"
        appendTo="body"
        styleClass="w-full"
        inputStyleClass="w-full"
        emptyMessage="Ningún arrendamiento de este grupo coincide"
        [showEmptyMessage]="true"
      >
        <ng-template let-row pTemplate="item">
          <div class="ns-suggestion">
            <b>{{ row.folio }}</b>
            <span>{{ row.clientName }}</span>
            <small>{{ row.assetName }}</small>
          </div>
        </ng-template>
      </p-autoComplete>
      <small class="ns-hint" *ngIf="!leases.length && !resolving"
        >Elige al menos uno. Se buscan dentro de la pestaña desde la que abriste.</small
      >
    </div>

    <div class="field field-flex">
      <label for="nsComments">Observaciones (opcional):</label>
      <textarea pInputTextarea id="nsComments" rows="2" [(ngModel)]="generalComments" [disabled]="submitting"></textarea>
    </div>

    <!-- Acta opcional: se sube UNA vez y queda ligada a todos. En registro es lo que se viene a hacer. -->
    <div class="field field-flex">
      <app-input-single-file
        [label]="isVoting ? 'Acta (opcional)' : 'Acta de la sesión'"
        [folder]="'committee'"
        [documentName]="'acta-comite'"
        [id]="'nueva'"
        [fileUrlValue]="minuteUrl"
        [disabled]="submitting"
        [tooltip]="'Archivo PDF con el acta. Queda ligada a todos los arrendamientos de la sesión.'"
        (fileUrl)="onMinuteSelected($event)"
        (fileDeleted)="onMinuteDeleted()"
      ></app-input-single-file>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button pButton type="button" label="Cancelar" class="p-button-text p-button-sm" [disabled]="submitting" (click)="close()"></button>
    <button
      pButton
      type="button"
      [label]="isVoting ? 'Abrir votación' : 'Registrar sesión'"
      [icon]="isVoting ? 'fa-solid fa-check-to-slot' : 'fa-regular fa-file-lines'"
      class="p-button-sm p-button-success"
      [disabled]="!canSubmit"
      [loading]="submitting"
      (click)="submit()"
    ></button>
  </ng-template>
</p-dialog>
